import React, { useState, useEffect } from "react";
import { Layout, Form, Button, Card, message, DatePicker } from "antd";
import { FileTextOutlined, RightOutlined } from "@ant-design/icons";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../../services/axiosInstance";
import dayjs from "dayjs";
import "../../../styles/fluidDesign.css";
import AppBreadcrumb from "../../../components/common/AppBreadcrumb";

const { Content } = Layout;

const CreateRequest = () => {
    const { id } = useParams(); // For edit mode
    const isEdit = !!id;

    const [loadingDetails, setLoadingDetails] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();
    const navigate = useNavigate();

    useEffect(() => {
        if (isEdit) {
            fetchDetails();
        }
    }, [id]);

    const fetchDetails = async () => {
        try {
            setLoadingDetails(true);
            const response = await api.get(`/inventory/material_request/get.php?mr_id=${id}`);
            if (response.data.status === "success" && response.data.dataset) {
                const data = response.data.dataset;
                if (data.req_status !== 'Draft' && data.req_status !== 'Rejected') {
                    message.error("Only draft or rejected material requests can be edited.");
                    navigate("/inventory/material-request/list");
                    return;
                }
                form.setFieldsValue({
                    required_date: data.required_date ? dayjs(data.required_date) : dayjs(),
                    mr_reason: data.mr_reason || data.reason_for_used || ""
                });
            } else {
                message.error(response.data.message || "Failed to fetch material request details.");
                navigate("/inventory/material-request/list");
            }
        } catch (error) {
            message.error("Error loading material request details.");
            navigate("/inventory/material-request/list");
        } finally {
            setLoadingDetails(false);
        }
    };

    const handleCreateOrUpdate = async (values) => {
        try {
            setSubmitting(true);
            const payload = {
                mr_reason: "",
                required_date: values.required_date ? values.required_date.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"),
                mr_status: "Draft",
                items: [] // Will add items in the next step
            };

            let response;
            if (isEdit) {
                payload.mr_id = Number(id);
                // Preserve current items during header update by fetching them first
                const detailRes = await api.get(`/inventory/material_request/get.php?mr_id=${id}`);
                if (detailRes.data.status === "success" && detailRes.data.dataset) {
                    payload.items = (detailRes.data.dataset.items || []).map(item => ({
                        in_id: Number(item.in_id),
                        request_qty: Number(item.request_qty)
                    }));
                }
                response = await api.post("/inventory/material_request/update.php", payload);
            } else {
                response = await api.post("/inventory/material_request/create.php", payload);
            }

            if (response.data.status === "success") {
                const targetId = isEdit ? id : response.data.mr_id;
                message.success(isEdit ? "Request header updated successfully." : "Material Request created successfully.");
                navigate(`/inventory/material-request/${targetId}/items`);
            } else {
                message.error(response.data.message || "Failed to save material request master.");
            }
        } catch (error) {
            message.error("An error occurred during submission.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <AppBreadcrumb items={[
                    { label: 'Inventory', link: '/inventory-items' },
                    { label: isEdit ? 'Edit Material Request' : 'Create Material Request' },
                ]} />
                <h1 className="fluid-page-title">
                    <FileTextOutlined style={{ marginRight: 12 }} />{isEdit ? "Edit Material Request" : "Create Material Request"}
                </h1>
            </div>

            <Content className="fluid-content" style={{ display: "flex", justifyContent: "center" }}>
                <Card className="fluid-card" style={{ width: "100%", maxWidth: "700px" }} title={isEdit ? "Edit Request Overview" : "New Request Overview"}>
                    <Form
                        form={form}
                        layout="vertical"
                        initialValues={{
                            required_date: dayjs()
                        }}
                        onFinish={handleCreateOrUpdate}
                    >
                        <Form.Item
                            name="required_date"
                            label="Required Date"
                            rules={[{ required: true, message: "Please select required date" }]}
                        >
                            <DatePicker
                                style={{ width: "100%" }}
                                className="fluid-input"
                                disabledDate={(current) => current && current < dayjs().startOf('day')}
                            />
                        </Form.Item>


                        <Form.Item style={{ marginBottom: 0, marginTop: 32 }}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                icon={<RightOutlined />}
                                loading={submitting || loadingDetails}
                                className="fluid-btn-primary"
                                block
                            >
                                Save & Proceed to Items
                            </Button>
                        </Form.Item>
                    </Form>
                </Card>
            </Content>
            <Footerdiv />
        </Layout>
    );
};

export default CreateRequest;
